import {
  computed,
  inject,
  INJECTOR,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { handleRequestError } from '@softline/application';
import { MessageBarService } from '@softline/ui-core';
import { GearRepository } from '../repositories/gear.repository';

interface GearListParams {
  query: string | null;
  limit: number | null;
  offset: number;
  customerId: number | undefined;
}

/** A cached page of gear rows for one (query, customer, limit, offset) window. */
interface GearPage<T> {
  items: T[];
  totalCount: number;
}

export abstract class GearService<T> {
  readonly messageBarService = inject(MessageBarService);
  readonly injector = inject(INJECTOR);
  readonly repository = inject(GearRepository<T>);

  readonly id = signal<number | null>(null);
  readonly customerId = signal<number | undefined>(undefined);
  readonly query = signal<string | null>(null);
  readonly limit = signal<number | null>(25);
  readonly offset = signal<number>(0);

  readonly #items = signal<T[]>([]);
  readonly #totalCount = signal<number>(0);

  private static readonly listParamsEqual = (
    a: GearListParams,
    b: GearListParams
  ): boolean =>
    a.query === b.query &&
    a.limit === b.limit &&
    a.offset === b.offset &&
    a.customerId === b.customerId;

  readonly #listParams = computed<GearListParams>(
    () => ({
      query: this.query(),
      limit: this.limit(),
      offset: this.offset(),
      customerId: this.customerId(),
    }),
    { equal: GearService.listParamsEqual }
  );

  // Pages are cached so paging back to a visited window is instant (same as
  // the orders list). Cleared on reload / mutation so stale rows don't linger.
  readonly #pageCache = new Map<string, GearPage<T>>();
  #loadVersion = 0;

  private static readonly pageCacheKey = (p: GearListParams): string =>
    `${p.query ?? ''}|${p.customerId ?? ''}|${p.limit}|${p.offset}`;

  // All Vehicles / Tools (the current page)
  readonly #allResource = resource({
    params: this.#listParams,
    loader: async ({ params }) => {
      const key = GearService.pageCacheKey(params);
      const cached = this.#pageCache.get(key);
      if (cached) {
        this.#items.set(cached.items);
        this.#totalCount.set(cached.totalCount);
        return cached.items;
      }
      try {
        const version = ++this.#loadVersion;
        const page = await this.repository.retrieve(
          params.query ?? undefined,
          params.limit ?? 25,
          params.customerId ?? undefined,
          params.offset
        );
        if (version !== this.#loadVersion) return this.#items();
        const items = ([...page.items] as unknown as T[]).sort(
          (a, b) => ((b as { id?: number }).id ?? 0) - ((a as { id?: number }).id ?? 0)
        );
        const sortedPage: GearPage<T> = { items, totalCount: page.totalCount };
        this.#pageCache.set(key, sortedPage);
        this.#items.set(items);
        this.#totalCount.set(page.totalCount);
        return items;
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return null;
      }
    },
  });

  readonly all = computed(() => this.#allResource.value());
  readonly totalCount = computed(() => this.#totalCount());
  readonly allLoading = computed(() => this.#allResource.isLoading());
  readonly allError = computed(
    () => !this.#allResource.isLoading() && this.#allResource.value() === null
  );

  #invalidatePageCache(): void {
    this.#pageCache.clear();
  }

  // Selected Vehicle
  readonly #detailResource = resource({
    params: () => this.id(),
    loader: async ({ params }) => {
      const vehicleId = params;
      if (!vehicleId) return null;
      try {
        return await this.repository.retrieveById(vehicleId);
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        return null;
      }
    },
  });

  readonly value = linkedSignal(() => this.#detailResource.value());
  readonly loading = computed(() => this.#detailResource.isLoading());

  async updateVehicle(vehicleId: string, body: Partial<T>) {
    try {
      const updatedVehicle = await this.repository.updateById(Number(vehicleId), body);
      this.value.set(updatedVehicle);
      this.#invalidatePageCache();
      this.messageBarService.success(
        'Das Fahrzeug/Kleingerät wurde erfolgreich aktualisiert.'
      );
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  loadVehicleById(id: number) {
    this.id.set(id);
  }

  loadVehiclesByCustomerId(customerId: number | undefined) {
    this.customerId.set(customerId);
  }

  async createVehicle(body: Partial<T>): Promise<T | null> {
    try {
      const entity = await this.repository.create(body);
      this.#invalidatePageCache();
      this.messageBarService.success('Wurde erfolgreich Erstellt.');
      return entity;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  async getVehicleById(id: number): Promise<T | null> {
    try {
      const entity = await this.repository.retrieveById(id);
      return entity;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  reload() {
    this.#invalidatePageCache();
    this.#allResource.reload();
  }

  abstract createForm(): FormGroup;
}
